/**
 * Размер бүрийн нөөц — салбарын самбарын гол блок.
 *
 * Хоёр хэсэг:
 *   1. Размерын хавтан — «40 размер хэд байна» гэдгийг нэг харцаар
 *   2. Анхаарах размер — дууссан/цөөн размер, агуулахад байгаа эсэх
 *
 * ⚠️ «Бага» гэдгийг ЭНД шийдэхгүй — сервер `sizeStockRows`-оор
 *    (`@boots/domain`) `level` тавьж ирүүлнэ. Мэдэгдлийн хонх ч мөн
 *    тэр талбарыг уншдаг тул хоёр газар өөр размер «бага» болохгүй.
 *
 * ⚠️ Өнгө ТӨЛӨВ заана: дууссан = ягаан (`alert`), цөөн = шар (`warn`),
 *    хэвийн = саарал. Хэвийнийг оюугаар будвал 20 хавтан бүгд
 *    тодорч, анхаарах хоёр нь дунд нь алга болно.
 */

import {
  formatQty, formatSize, LOW_STOCK_COVER_DAYS, LOW_STOCK_UNITS, SALES_WINDOW_DAYS,
  type SizeStockRow, type StockLevel,
} from '@boots/domain';
import { Section } from '../section';
import { EmptyState } from '../empty-state';

const TILE: Record<StockLevel, string> = {
  out: 'border-alert-bg-2 bg-alert-bg',
  low: 'border-warn-bg bg-warn-bg',
  ok: 'border-hair bg-panel',
};

const TAG: Record<StockLevel, { text: string; cls: string } | null> = {
  out: { text: 'Дууссан', cls: 'text-alert-ink' },
  low: { text: 'Цөөн', cls: 'text-warn-ink' },
  ok: null,
};

export function SizeStockCard({ sizes }: { sizes: SizeStockRow[] }) {
  const watch = sizes.filter((s) => s.level !== 'ok');
  const onHand = sizes.reduce((n, s) => n + s.onHand, 0);

  return (
    <Section
      title="Размер бүрээр"
      subtitle={`салбарт ${formatQty(onHand)} ш · ${formatQty(sizes.length)} размер`}
      note={
        watch.length > 0
          ? `${formatQty(watch.length)} размер анхаарах`
          : sizes.length > 0 ? 'бүх размер хангалттай' : undefined
      }
    >
      {sizes.length === 0 ? (
        <EmptyState
          compact
          title="Салбарт зарагдах гутал алга"
          description="Агуулахаас шилжүүлэг ирж, хүлээн авмагц размер бүрээр энд гарна."
        />
      ) : (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-8 2xl:grid-cols-10">
          {sizes.map((s) => <SizeTile key={s.size ?? 'none'} s={s} />)}
        </ul>
      )}

      {watch.length > 0 ? <WatchList rows={watch} /> : null}

      <p className="text-micro text-ink-3 text-pretty">
        Цөөн = {LOW_STOCK_UNITS} ба түүнээс доош, эсвэл сүүлийн {SALES_WINDOW_DAYS} хоногийн
        борлуулалтаар {LOW_STOCK_COVER_DAYS} хоногт хүрэхгүй. Лангуу ба агуулах өрөөний гутал хамт
        тоологдоно.
      </p>
    </Section>
  );
}

function SizeTile({ s }: { s: SizeStockRow }) {
  const tag = TAG[s.level];
  return (
    <li
      className={`flex flex-col gap-0.5 rounded-well border px-3 py-2.5 ${TILE[s.level]}`}
      title={
        `${formatSize(s.size)} размер: салбарт ${formatQty(s.onHand)} ш · ` +
        `агуулахад ${formatQty(s.warehouse)} ш · ${SALES_WINDOW_DAYS} хоногт ${formatQty(s.sold)} зарагдсан`
      }
    >
      <span className="flex items-baseline justify-between gap-1">
        <span className="num text-h2">{formatSize(s.size)}</span>
        {tag ? <span className={`text-micro font-medium ${tag.cls}`}>{tag.text}</span> : null}
      </span>
      <span className="num text-metric-sm">
        {formatQty(s.onHand)}
        <span className="ml-0.5 text-meta text-ink-2">ш</span>
      </span>
      <span className="num text-micro text-ink-3">агуулахад {formatQty(s.warehouse)}</span>
    </li>
  );
}

/**
 * Анхаарах размер — ажилтанд ЮУ ХИЙХИЙГ хэлнэ. Агуулахад байвал
 * «татуулна», байхгүй бол «агуулахад ч алга» — хоёр өөр ажил.
 */
function WatchList({ rows }: { rows: SizeStockRow[] }) {
  return (
    <div className="flex flex-col gap-1.5 border-t border-hair pt-3.5">
      <span className="text-meta text-ink-2">Дуусч буй размер</span>
      <ul className="flex flex-col gap-1">
        {rows.map((s) => (
          <li
            key={s.size ?? 'none'}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 rounded-well px-2 py-1.5 hover:bg-row-hover"
          >
            <span className="num w-10 shrink-0 text-h3">{formatSize(s.size)}</span>
            <span className={`text-meta ${s.level === 'out' ? 'text-alert-ink' : 'text-warn-ink'}`}>
              {s.onHand === 0 ? 'дууссан' : `${formatQty(s.onHand)} ш үлдсэн`}
              {s.coverDays !== null && s.onHand > 0 ? ` · ≈${formatQty(s.coverDays)} хоногт дуусна` : ''}
            </span>
            <span className="text-meta text-ink-2">
              {s.sold > 0 ? `${SALES_WINDOW_DAYS} хоногт ${formatQty(s.sold)} зарагдсан` : 'сүүлд зарагдаагүй'}
            </span>
            <span className={`ml-auto text-meta ${s.warehouse > 0 ? 'text-accent-700' : 'text-ink-3'}`}>
              {s.warehouse > 0 ? `агуулахад ${formatQty(s.warehouse)} ш — татуулж болно` : 'агуулахад ч алга'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
